import React from 'react';
import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';

const styles = StyleSheet.create({
  page: {
    backgroundColor: '#ffffff',
    padding: 40,
    fontSize: 10,
    fontFamily: 'Helvetica', 
    position: 'relative',
  },
  header: {
    textAlign: 'center',
    fontSize: 12,
    fontWeight: 'bold',
    fontFamily: 'Helvetica-Bold',
    marginBottom: 4,
  },
  institutionInfo: {
    marginBottom: 8,
    marginTop: 8,
    fontFamily: 'Helvetica-Bold',
  },
  infoRow: {
    flexDirection: 'row',
    marginTop: 4,
    fontFamily: 'Helvetica-Bold',
    fontWeight: 'bold',
  },
  infoLabel: {
    width: '100%',
    fontFamily: 'Helvetica-Bold',
    fontWeight: 'bold',
    fontSize: 10
  },
  table: {
    display: 'table',
    width: 'auto',
    borderStyle: 'solid',
    borderWidth: 1,
    borderRightWidth: 0,
    borderBottomWidth: 0,
    marginBottom: 20
  },
  tableRow: {
    flexDirection: 'row',
    minHeight: 24
  },
  tableCell: {
    borderStyle: 'solid',
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    padding: 8,
    fontSize: 10,
    verticalAlign: 'middle'
  },
  srNoColumn: {
    width: '5%',
  },
  descriptionColumn: {
    width: '60%',
  },
  detailsColumn: {
    width: '35%',
  },
  tableHeader: {
    backgroundColor: '#f5f5f5',
    fontWeight: 'bold'
  },
  mergedCell: {
    width: '95%',
    padding: 8,
    borderStyle: 'solid',
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
  },
  nestedTableWrapper: {
    margin: 8,
  },
  nestedTable: {
    width: '70%',
    borderStyle: 'solid',
    borderWidth: 1,
  },
  nestedTableCell: {
    padding: 6,
    fontSize: 9,
    borderStyle: 'solid',
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    width: '33.33%'
  },
  nestedTableHeader: {
    backgroundColor: '#f8f8f8',
    fontWeight: 'bold'
  },
  signatureSection: {
    marginTop: 30,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    width: '100%',
    paddingHorizontal: 20
  },
  footer: {
    position: 'absolute',
    bottom: 20,
    left: 0,
    right: 0,
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    fontSize: 8,
    borderTopWidth: 1,
    borderTopColor: '#ccc',
    paddingTop: 4
  }
});

const SeniorCollegePlacementResultPDF = ({ result = [], info = {}, isContinuation = false, remainingData = null }) => {
  // Safe data handling
  const safeResult = Array.isArray(result) ? result : [];
  const safeRemainingData = Array.isArray(remainingData) ? remainingData : null;
  const safeInfo = typeof info === 'object' ? info : {};

  // Format date safely
  const formatDate = (dateString) => {
    try {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid Date';
      
      return [
        String(date.getDate()).padStart(2, '0'),
        String(date.getMonth() + 1).padStart(2, '0'),
        date.getFullYear()
      ].join('/');
    } catch (error) {
      return 'N/A';
    }
  };

  const currentDate = formatDate(new Date());
  
  // Data processing with safety checks
  const allData = isContinuation ? safeRemainingData || [] : safeResult;
  const firstPageMaxNestedRows = 5;
  const itemsPerPage = 8;
  
  // Ensure allData is always an array
  const safeAllData = Array.isArray(allData) ? allData : [];
  
  // Pagination chunks
  const firstPageChunk = safeAllData.slice(0, firstPageMaxNestedRows);
  const continuationChunks = [];
  
  if (!isContinuation) {
    const remaining = safeAllData.slice(firstPageMaxNestedRows);
    for (let i = 0; i < remaining.length; i += itemsPerPage) {
      continuationChunks.push(remaining.slice(i, i + itemsPerPage));
    }
  }

  const totalPages = continuationChunks.length + 1;

  // Main table rows data with fallbacks
  const mainTableRows = [
    { sr: 1, desc: `Designation of the post in which pay is to be fixed as on ${formatDate(safeInfo.placementDate)}`, 
      info: safeInfo.revisedDesignation || 'N/A' },
    { sr: 2, desc: 'Status (Substantive/officiating)', info: 'Substantive' },
    { sr: 3, desc: 'Date of Placement', info: formatDate(safeInfo.placementDate) },
    { sr: 4, desc: 'Pre-revised Designation', info: safeInfo.existingDesignation || 'N/A' },
    { sr: 5, desc: 'Pre-revised Academic Level & pay Matrix Scale', info: safeInfo.existingPayLevel || 'N/A' },
    { sr: 6, desc: 'Pre-revised Pay in the pay matrix scale as on date of placement', 
      info: safeInfo.existingPayInLevel || 'N/A' },
    { sr: 7, desc: 'Pay after the notional increment in the Pre-revised scale in Matrix', 
      info: safeInfo.afterNotionalIncrement || 'N/A' },
    { sr: 8, desc: 'Revised Pay Scale in the Pay Matrix & Academic Level of Promotion Post', 
      info: safeInfo.revisedPayLevel || 'N/A' },
    { sr: 9, desc: 'Applicable Cell in the Level either equal to or just above the Amount at Sr. No. 6 (For Promotion)', 
      info: safeInfo.revisedPay || 'N/A' },
    { sr: 10, desc: 'Date of next increment and pay after grant of increment', 
      info: `${formatDate(safeInfo.nextApplicableDate)} - ${safeAllData[0]?.[2] || 'N/A'}` },
  ];

  // Render nested table row safely
  const renderNestedRow = (row, index) => {
    if (!Array.isArray(row) || row.length < 3) return null;
    return (
      <View style={styles.tableRow} key={index}>
        <Text style={styles.nestedTableCell}>{formatDate(row[0])}</Text>
        <Text style={styles.nestedTableCell}>{row[1] || 'N/A'}</Text>
        <Text style={styles.nestedTableCell}>{row[2] || 'N/A'}</Text>
      </View>
    );
  };

  return (
    <Document>
      {/* First Page with Complete Table */}
      {!isContinuation && (
        <Page style={styles.page}>
          {/* Header Section */}
          <View>
            <Text style={[styles.header, { fontSize: 12 }]}>Accompaniment to Government Resolution,</Text>
            <Text style={[styles.header, { fontSize: 9 }]}>Higher and Technical Education Department No. Resolution No. Misc-2018/C.R.56/18/UNI-1, dated 08 March, 2019.</Text>
            <Text style={[styles.header, { fontSize: 12 }, { marginTop: 8 }]}>PROFORMA FOR FIXATION OF PAY on Promotion In 7th Pay</Text>
          </View>

          {/* Institution Information */}
          <View style={styles.institutionInfo}>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Name of the College/Institution: {safeInfo.officeName || 'N/A'}</Text>
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Name of the teacher: {safeInfo.employeeName || 'N/A'}</Text>
            </View>
          </View>

          {/* Main Table */}
          <View style={styles.table}>
            {/* Table Header */}
            <View style={[styles.tableRow, styles.tableHeader]}>
              <Text style={[styles.tableCell, styles.srNoColumn]}>Sr No</Text>
              <Text style={[styles.tableCell, styles.descriptionColumn]}>Description</Text>
              <Text style={[styles.tableCell, styles.detailsColumn]}>Relevant Information</Text>
            </View>

            {/* Main Table Rows */}
            {mainTableRows.map((row, index) => (
              <View style={styles.tableRow} key={index}>
                <Text style={[styles.tableCell, styles.srNoColumn]}>{row.sr}</Text>
                <Text style={[styles.tableCell, styles.descriptionColumn]}>{row.desc}</Text>
                <Text style={[styles.tableCell, styles.detailsColumn]}>{row.info}</Text>
              </View>
            ))}

            {/* Row 11 - Nested Table */}
            <View style={styles.tableRow}>
              <Text style={[styles.tableCell, styles.srNoColumn]}>11</Text>
              <View style={styles.mergedCell}>
                <Text style={{ fontWeight: 'bold', marginBottom: 6 }}>Pay after increment in applicable Level of pay Matrix</Text>
                <View style={styles.nestedTable}>
                  <View style={[styles.tableRow, styles.nestedTableHeader]}>
                    <Text style={styles.nestedTableCell}>Increment Date</Text>
                    <Text style={styles.nestedTableCell}>Pay Level</Text>
                    <Text style={styles.nestedTableCell}>Amount</Text>
                  </View>
                  {firstPageChunk.map(renderNestedRow)}
                </View>
              </View>
            </View>

            {/* Remarks Row (12) - Only if single page */}
            {continuationChunks.length === 0 && (
              <View style={styles.tableRow}>
                <Text style={[styles.tableCell, styles.srNoColumn]}>12</Text>
                <Text style={[styles.tableCell, styles.descriptionColumn]}>Remarks</Text>
                <Text style={[styles.tableCell, styles.detailsColumn]}>{safeInfo.remark || 'No remarks'}</Text>
              </View>
            )}
          </View>
            
          {/* Signature Section */}
          {continuationChunks.length === 0 && (
            <View style={[styles.signatureSection, { 
              marginTop: 4,
              paddingTop: 5
            }]}>
              <View>
                <Text>Date: _________________________</Text>
                <Text style={{ marginTop: 12 }}>Place: _________________________</Text>
              </View>
              <Text>Principal</Text>
            </View>
          )}

          {/* Footer */}
          <View style={styles.footer}>
            <Text>{currentDate}</Text>
            <Text>himanshusalarycorner.in</Text>
            <Text>Page 1 of {totalPages}</Text>
          </View>
        </Page>
      )}

      {/* Continuation Pages */}
      {continuationChunks.map((chunk, pageIndex) => (
        <Page key={pageIndex} style={styles.page}>
          <View style={styles.table}>
            {/* Continued Row 11 */}
            <View style={styles.tableRow}>
              <Text style={[styles.tableCell, styles.srNoColumn]}>11</Text>
              <View style={styles.mergedCell}>
                <Text style={{ fontWeight: 'bold', marginBottom: 6 }}>Pay after increment in applicable Level of pay Matrix (Continued)</Text>
                <View style={styles.nestedTable}>
                  <View style={[styles.tableRow, styles.nestedTableHeader]}>
                    <Text style={styles.nestedTableCell}>Increment Date</Text>
                    <Text style={styles.nestedTableCell}>Pay Level</Text>
                    <Text style={styles.nestedTableCell}>Amount</Text>
                  </View>
                  {chunk.map(renderNestedRow)}
                </View>
              </View>
            </View>

            {/* Row 12 on Last Page */}
            {pageIndex === continuationChunks.length - 1 && (
              <View style={styles.tableRow}>
                <Text style={[styles.tableCell, styles.srNoColumn]}>12</Text>
                <Text style={[styles.tableCell, styles.descriptionColumn]}>Remarks</Text>
                <Text style={[styles.tableCell, styles.detailsColumn]}>{safeInfo.remark || 'No remarks'}</Text>
              </View>
            )}
          </View>

          {/* Signature Section */}
          {pageIndex === continuationChunks.length - 1 && (
            <View style={styles.signatureSection}>
              <View>
                <Text>Date: _________________________</Text>
                <Text style={{ marginTop: 12}}>Place: _________________________</Text>
              </View>
              <Text style={{ marginLeft: 20 }}>Principal</Text>
            </View>
          )}

          {/* Footer */}
          <View style={styles.footer}>
            <Text>{currentDate}</Text>
            <Text>himanshusalarycorner.in</Text>
            <Text>Page {pageIndex + 2} of {totalPages}</Text>
          </View>
        </Page>
      ))}
    </Document>
  );
};

export default SeniorCollegePlacementResultPDF;